/**
 * Asset Management Portal — Dashboard page.
 * Overview of custom assessment domains with quick stats, asset summary,
 * and a collapsible user guide explaining all portal pages.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Grid, Paper, Box, CircularProgress, Alert, Chip,
  Accordion, AccordionSummary, AccordionDetails, Stack, Divider,
} from '@mui/material';
import {
  Assignment, Category, TrendingUp, Inventory, Upload, HourglassEmpty,
  ExpandMore, MenuBook, Dashboard as DashboardIcon, CloudUpload,
  RateReview, Visibility, Security,
} from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';
import PortalTooltip from '../../../components/PortalTooltip';

/* ── User guide content ──────────────────────────────────────── */

const GUIDE_SECTIONS = [
  {
    title: 'Dashboard',
    icon: <DashboardIcon fontSize="small" sx={{ color: '#098E7E' }} />,
    description: 'This page. Provides a quick overview of your assessment domains, uploaded assets, pending approvals, and client document submissions. Use the stat cards to monitor portal activity at a glance.',
  },
  {
    title: 'Manage Domains',
    icon: <Assignment fontSize="small" sx={{ color: '#098E7E' }} />,
    description: 'Create and configure custom assessment domains. Each domain includes a name, category, description, and AI agent assignment. Add assessment questions manually, via CSV upload, or let the AI agent generate them. Upload domain-specific reference assets (PDFs, Excel, JSON) and index them for AI Search so the agent can use them during assessments. Edit or replace assets inline — JSON files can be edited directly in the browser.',
  },
  {
    title: 'Upload Assets',
    icon: <CloudUpload fontSize="small" sx={{ color: '#098E7E' }} />,
    description: 'Publish KPMG materials directly using your asset-management authority. Sales pitches and report templates appear as view-only in the client Asset Library for permitted domains. Questions and tools remain internal.',
  },
  {
    title: 'Review SME Assets',
    icon: <RateReview fontSize="small" sx={{ color: '#098E7E' }} />,
    description: 'Review, approve, or reject assets uploaded by team members. Use the tabs to filter by status (All, Pending, Approved, Rejected). Add review notes when approving or rejecting. Approved assets become available for client engagements.',
  },
  {
    title: 'Client Uploaded Documents',
    icon: <Visibility fontSize="small" sx={{ color: '#098E7E' }} />,
    description: 'View and download private supporting documents from clients in your assigned engagements. These documents support KPMG discovery and assessment, do not bypass the questionnaire, and are never published to the shared client Asset Library.',
  },
  {
    title: 'Compliance & ERA',
    icon: <Security fontSize="small" sx={{ color: '#098E7E' }} />,
    description: 'Manage compliance rules and generate Engagement Risk Assessment (ERA) reports. This page has three tabs:\n\n' +
      '• Compliance Rules — View, edit, add, and delete compliance rules. Upload rules from JSON, DOCX, PDF, or Excel files. Rules are auto-seeded from the compliance rules JSON asset on first use.\n\n' +
      '• Generate ERA — Select a client-uploaded document, optionally provide extra rules via the chat prompt, then generate an ERA report. The AI agent compares the document against all active compliance rules and returns structured findings with risk scores.\n\n' +
      '• Previous Reports — View past ERA reports with risk ratings, finding counts, and expiry dates.\n\n' +
      'To update the base compliance rules, edit the "complaince rules.json" asset under Manage Domains → Assets tab, or upload a new rules file on the Compliance Rules tab.',
  },
];

const AssetManagementDashboard: React.FC = () => {
  const [domains, setDomains] = useState<any[]>([]);
  const [guideOpen, setGuideOpen] = useState(false);
  const [assetStats, setAssetStats] = useState({ total: 0, pending: 0, approved: 0 });
  const [clientUploadCount, setClientUploadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch domains
      const domainRes = await authFetch('/api/custom-domains');
      const domainData = await domainRes.json();
      setDomains(domainData.domains || []);

      // Fetch asset stats (all SME uploads)
      try {
        const assetRes = await authFetch('/api/admin/portal/assets');
        const assetData = await assetRes.json();
        if (!assetRes.ok || !assetData.success) throw new Error(assetData.error || 'Failed to load asset statistics');
        if (assetData.success) {
          const assets = assetData.assets || [];
          setAssetStats({
            total: assets.length,
            pending: assets.filter((a: any) => a.status === 'pending').length,
            approved: assets.filter((a: any) => a.status === 'approved').length,
          });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load asset statistics');
      }

      // Fetch client upload count
      try {
        const clientRes = await authFetch('/api/sme/assets/client-uploads');
        const clientData = await clientRes.json();
        if (!clientRes.ok || !clientData.success) throw new Error(clientData.error || 'Failed to load client documents');
        if (clientData.success) {
          setClientUploadCount((clientData.assets || []).length);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load client documents');
      }

      setLastRefreshed(new Date());
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalDomains = domains.length;
  const categories = Array.from(new Set(domains.map((d: any) => d.category)));
  const totalQuestions = domains.reduce((sum: number, d: any) => sum + (d.question_count || 0), 0);

  const statCards = [
    { label: 'Total Domains', value: totalDomains, icon: <Assignment sx={{ fontSize: 40 }} />, color: '#098E7E' },
    { label: 'Categories', value: categories.length, icon: <Category sx={{ fontSize: 40 }} />, color: '#483698' },
    { label: 'Total Questions', value: totalQuestions, icon: <TrendingUp sx={{ fontSize: 40 }} />, color: '#009A44' },
    { label: 'Total Assets', value: assetStats.total, icon: <Inventory sx={{ fontSize: 40 }} />, color: '#00338D' },
    { label: 'Pending Approval', value: assetStats.pending, icon: <HourglassEmpty sx={{ fontSize: 40 }} />, color: '#ed6c02' },
    { label: 'Client Uploaded Documents', value: clientUploadCount, icon: <Upload sx={{ fontSize: 40 }} />, color: '#C6007E' },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <PortalTooltip tooltipKey="asset_management.page.dashboard" showIcon>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#098E7E' }}>
            Asset Management Dashboard
          </Typography>
        </PortalTooltip>
        <RefreshButton onRefresh={fetchData} lastRefreshed={lastRefreshed} />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((card) => (
          <Grid item xs={12} sm={6} md={4} key={card.label}>
            <Paper elevation={2} sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ color: card.color }}>{card.icon}</Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{card.value}</Typography>
                <Typography variant="body2" color="text.secondary">{card.label}</Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* ── User Guide ──────────────────────────────────────── */}
      <Accordion
        expanded={guideOpen}
        onChange={() => setGuideOpen(!guideOpen)}
        sx={{ mb: 3, border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}
        elevation={0}
      >
        <AccordionSummary expandIcon={<ExpandMore />} sx={{ backgroundColor: guideOpen ? '#f0faf8' : 'transparent' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MenuBook sx={{ color: '#098E7E' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Portal User Guide
            </Typography>
            <Chip label="How to use this portal" size="small" variant="outlined" sx={{ ml: 1 }} />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The Asset Management Portal allows you to create assessment domains, manage reference assets,
            review uploads, and run Engagement Risk Assessments powered by AI. Here is what each page does:
          </Typography>
          <Stack spacing={0}>
            {GUIDE_SECTIONS.map((section, idx) => (
              <Box key={section.title}>
                {idx > 0 && <Divider />}
                <Box sx={{ py: 1.5, px: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {section.icon}
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{section.title}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', pl: 3.5 }}>
                    {section.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Recent Domains</Typography>
      <Grid container spacing={2}>
        {domains.slice(0, 6).map((domain: any) => (
          <Grid item xs={12} sm={6} md={4} key={domain.domain_id}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{domain.display_name}</Typography>
              <Chip label={domain.category} size="small" sx={{ mt: 0.5, mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {domain.question_count || 0} questions
              </Typography>
            </Paper>
          </Grid>
        ))}
        {domains.length === 0 && (
          <Grid item xs={12}>
            <Alert severity="info">No custom domains yet. Use Manage Domains to create your first domain.</Alert>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default AssetManagementDashboard;
